import { getDb } from './db';
import type { Plan, PlanSession, Task, HistoryEntry, NotificationSettings, Preferences, SyncMeta } from '$lib/domain/types';
import { normalizePreferences } from '$lib/domain/preferences';
import { normalizeNotificationSettings } from '$lib/domain/notifications';

const nowIso = () => new Date().toISOString();

export const taskRepo = {
  async list() {
    const db = await getDb();
    return db.getAll('tasks');
  },
  async upsert(task: Task) {
    const db = await getDb();
    await db.put('tasks', { ...task, updatedAt: nowIso() });
  },
  async remove(id: string) {
    const db = await getDb();
    await db.delete('tasks', id);
  }
};

export const planRepo = {
  async list() {
    const db = await getDb();
    return db.getAllFromIndex('plans', 'by-start');
  },
  async byStatus(status: Plan['status']) {
    const db = await getDb();
    return db.getAllFromIndex('plans', 'by-status', status);
  },
  async upsert(plan: Plan) {
    const db = await getDb();
    await db.put('plans', { ...plan, updatedAt: nowIso() });
  },
  async remove(id: string) {
    const db = await getDb();
    await db.delete('plans', id);
  }
};

export const planSessionRepo = {
  async listAll() {
    const db = await getDb();
    return db.getAll('plan_sessions');
  },
  async list(planId: string) {
    const db = await getDb();
    const sessions = await db.getAllFromIndex('plan_sessions', 'by-plan', planId);
    return sessions.sort((a, b) => a.index - b.index);
  },
  async bulkUpsert(sessions: PlanSession[]) {
    const db = await getDb();
    const tx = db.transaction('plan_sessions', 'readwrite');
    await Promise.all(sessions.map((session) => tx.store.put(session)));
    await tx.done;
  },
  async removeForPlan(planId: string) {
    const db = await getDb();
    const sessions = await this.list(planId);
    if (!sessions.length) return [];
    const tx = db.transaction('plan_sessions', 'readwrite');
    await Promise.all(sessions.map((session) => tx.store.delete(session.id)));
    await tx.done;
    return sessions;
  },
  async replaceForPlan(planId: string, sessions: PlanSession[]) {
    const removed = await this.removeForPlan(planId);
    await this.bulkUpsert(sessions);
    return removed;
  }
};

export const historyRepo = {
  async list(planId?: string) {
    const db = await getDb();
    if (!planId) return db.getAll('history');
    return db.getAllFromIndex('history', 'by-plan', planId);
  },
  async add(entry: HistoryEntry) {
    const db = await getDb();
    await db.put('history', entry);
  }
};

export const preferencesRepo = {
  async get(): Promise<Preferences> {
    const db = await getDb();
    const record = await db.get('preferences', 'default');
    return normalizePreferences(record?.value);
  },
  async set(preferences: Preferences) {
    const db = await getDb();
    await db.put('preferences', { key: 'default', value: normalizePreferences(preferences) });
  }
};

export const notificationRepo = {
  async get(): Promise<NotificationSettings> {
    const db = await getDb();
    const record = await db.get('notification_settings', 'default');
    return normalizeNotificationSettings(record?.value);
  },
  async set(settings: NotificationSettings) {
    const db = await getDb();
    await db.put('notification_settings', { key: 'default', value: normalizeNotificationSettings(settings) });
  }
};

export const syncMetaRepo = {
  async get(clientId: string): Promise<SyncMeta> {
    const db = await getDb();
    return (
      (await db.get('meta', clientId)) ?? {
        clientId,
        lastSyncAt: undefined,
        syncToken: undefined
      }
    );
  },
  async set(meta: SyncMeta) {
    const db = await getDb();
    await db.put('meta', meta);
  }
};
